import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useScenarioMode } from "../../context/ScenarioModeContext";
import { ScenarioBanner, ScenarioToggle } from "../../components/planning/ScenarioModeControls";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import PersonnelCostDetailPanel from "../../components/planning/PersonnelCostDetailPanel";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const g = (obj, m) => obj?.[m] ?? obj?.[String(m)];
const MISSING_LABELS = {
  EMPLOYMENT_TERMS: "Missing Employment Terms",
  FTE_SALARY: "Missing FTE Monthly Salary",
  PENSION: "Missing Pension Amount",
};

export default function PersonnelCostsPage() {
  const { user, isAdmin } = useAuth();
  const { scenarioMode } = useScenarioMode();
  const canView = true; // all authenticated roles with company scope may view

  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [detail, setDetail] = useState(null); // { employee, month }

  useEffect(() => {
    api.get("/companies")
      .then((res) => {
        setCompanies(res.data);
        if (res.data.length > 0 && !res.data.some((co) => co.id === companyId)) setCompanyId(res.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  const loadCosts = async () => {
    if (!companyId || !year) return;
    setLoading(true);
    try {
      const res = await api.get(scenarioMode ? "/scenario-outcome/personnel-costs" : "/personnel-costs", { params: { company_id: companyId, year } });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, year, scenarioMode]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.employees.filter((e) => {
      if (typeFilter !== "ALL" && e.person.cost_classification !== typeFilter) return false;
      if (q && !e.person.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, search, typeFilter]);

  const summary = data?.summary;

  return (
    <div data-testid="personnel-costs-page">
      <PageHeader
        title="Personnel Costs"
        subtitle="Monthly employment cost — calculated from effective-dated terms and financial parameters"
        actions={
          <div className="flex items-center gap-2">
            <ScenarioToggle />
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="pc-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="pc-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`pc-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />
      <ScenarioBanner meta={data?.scenario_meta} />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="pc-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before planning."
            testId="pc-no-years-state"
          />
        </div>
      )}

      {year && data && summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="pc-summary">
          {[
            { label: "Expected Personnel Cost", value: summary.expected_personnel_cost_fy, suffix: " kr", strong: true, testId: "pc-summary-total" },
            { label: "Consultant Personnel Cost", value: summary.consultant_fy, suffix: " kr", testId: "pc-summary-consultant" },
            { label: "Overhead Personnel Cost", value: summary.overhead_fy, suffix: " kr", testId: "pc-summary-overhead" },
            { label: "Employee Headcount", value: summary.headcount_latest, suffix: "", testId: "pc-summary-headcount" },
            { label: "Avg Production Cost / Hour", value: summary.avg_production_cost_per_hour, suffix: " kr/h", testId: "pc-summary-rate" },
          ].map((kpi) => (
            <div
              key={kpi.label}
              className={`rounded-lg border p-3 ${kpi.strong ? "bg-[#0052CC] border-[#0052CC]" : "bg-white border-slate-200"}`}
              data-testid={kpi.testId}
            >
              <div className={`text-[11px] font-semibold uppercase tracking-wider ${kpi.strong ? "text-blue-100" : "text-slate-500"}`}>
                {kpi.label}
              </div>
              <div className={`text-lg font-bold mt-0.5 ${kpi.strong ? "text-white" : "text-slate-900"}`}>
                {kpi.value == null ? "N/A" : `${fmtSEK(kpi.value)}${kpi.suffix}`}
              </div>
            </div>
          ))}
        </div>
      )}
      {summary?.fy_completeness_note && (
        <div className="mb-4 flex items-start gap-2 bg-secondary/50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600" data-testid="pc-fy-completeness">
          <Lock className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
          {summary.fy_completeness_note}
        </div>
      )}

      {year && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Input
              className="w-64 bg-white"
              placeholder="Search employee…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="pc-search-input"
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-44 bg-white" data-testid="pc-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All employees</SelectItem>
                <SelectItem value="CONSULTANT">Consultants</SelectItem>
                <SelectItem value="OVERHEAD">Overhead</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="pc-grid">
            {loading && (
              <div className="py-16 flex justify-center" data-testid="pc-loading">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            )}
            {!loading && data && (
              <table className="w-full min-w-[1300px] border-collapse">
                <thead>
                  <tr className="bg-secondary/50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-secondary/50 min-w-56 z-10">Employee</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-24">Type</th>
                    {MONTHS.map((label, i) => {
                      const closed = g(data.periods, i + 1)?.status === "CLOSED";
                      return (
                        <th key={label} className={`text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-1 w-24 ${closed ? "text-slate-400 bg-slate-100/70" : "text-slate-500"}`}>
                          <div className="flex items-center justify-end gap-1 pr-1.5">
                            {closed && <Lock className="h-3 w-3" />}
                            {label}
                          </div>
                        </th>
                      );
                    })}
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28">Expected FY</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.person.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`pc-row-${e.person.id}`}>
                      <td className="py-2 px-3 sticky left-0 bg-white z-10">
                        <span className="text-sm font-semibold text-slate-900">{e.person.name}</span>
                        {e.person.scenario && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#C2410C] border border-[#FDBA74]" data-testid={`pc-scenario-badge-${e.person.id}`}>Scenario</span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs text-slate-500">{e.person.cost_classification === "CONSULTANT" ? "Consultant" : "Overhead"}</span>
                      </td>
                      {MONTHS.map((_, i) => {
                        const m = i + 1;
                        const cell = g(e.months, m) || {};
                        if (!cell.applicable) {
                          return (
                            <td key={m} className="py-2 px-1 text-right" data-testid={`pc-cell-${e.person.id}-${m}`}>
                              <div className="h-8 flex items-center justify-end pr-1.5 text-xs text-slate-300">·</div>
                            </td>
                          );
                        }
                        return (
                          <td key={m} className={`py-2 px-1 text-right ${cell.closed ? "bg-slate-100/70" : ""}`} data-testid={`pc-cell-${e.person.id}-${m}`}>
                            <button
                              className="h-8 w-full flex items-center justify-end gap-1 pr-1.5 text-sm hover:bg-[#EBF3FE] rounded transition-colors"
                              onClick={() => setDetail({ employee: e, month: m })}
                              data-testid={`pc-detail-${e.person.id}-${m}`}
                            >
                              {cell.incomplete ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={0} className="inline-flex" data-testid={`pc-incomplete-${e.person.id}-${m}`}>
                                      <AlertTriangle className="h-3.5 w-3.5 text-[#B45309]" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {(cell.missing || [])
                                      .map((x) => (x.startsWith("PARAMETER:") ? `Missing parameter: ${data.param_labels?.[x.slice(10)] || x.slice(10)}` : MISSING_LABELS[x] || x))
                                      .join(" · ")}
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <>
                                  {cell.override && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span tabIndex={0} className="inline-flex" data-testid={`pc-override-flag-${e.person.id}-${m}`}>
                                          <span className="h-2 w-2 rounded-full bg-[#B45309]" />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Override active: {fmtSEK(cell.override.override_amount)} kr (calculated {fmtSEK(cell.total_calculated)} kr) — {cell.override.comment}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                  <span className={cell.override ? "font-semibold text-[#B45309]" : "text-slate-800 font-medium"}>
                                    {fmtSEK(cell.active_amount)}
                                  </span>
                                </>
                              )}
                            </button>
                          </td>
                        );
                      })}
                      <td className="py-2 px-2 text-right text-sm font-bold text-slate-900" data-testid={`pc-fy-${e.person.id}`}>{fmtSEK(e.fy)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-50/60" data-testid="pc-total-row">
                    <td className="py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-secondary/50 z-10" colSpan={2}>Total personnel cost</td>
                    {MONTHS.map((_, i) => {
                      const m = i + 1;
                      const mm = g(data.monthly, m) || {};
                      return (
                        <td key={m} className="py-2 px-1 text-right" data-testid={`pc-total-${m}`}>
                          <div className="h-8 flex items-center justify-end gap-1 pr-1.5 text-xs font-bold text-slate-800">
                            {mm.incomplete > 0 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} className="inline-flex" data-testid={`pc-total-incomplete-${m}`}>
                                    <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{mm.incomplete} employee(s) incomplete this month — totals exclude them</TooltipContent>
                              </Tooltip>
                            )}
                            {fmtSEK(mm.total)}
                          </div>
                        </td>
                      );
                    })}
                    <td className="py-2 px-2 text-right text-sm font-extrabold text-slate-900" data-testid="pc-total-fy">{fmtSEK(summary?.expected_personnel_cost_fy)}</td>
                  </tr>
                  <tr className="bg-slate-50/60" data-testid="pc-headcount-row">
                    <td className="py-1.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-secondary/50 z-10" colSpan={2}>Employee headcount</td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className="py-1.5 px-1 text-right text-xs text-slate-600 pr-2.5" data-testid={`pc-headcount-${i + 1}`}>
                        {g(data.monthly, i + 1)?.headcount ?? 0}
                      </td>
                    ))}
                    <td className="py-1.5 px-2" />
                  </tr>
                </tbody>
              </table>
            )}
            {!loading && data && filtered.length === 0 && (
              <EmptyState title="No employees match" description="Personnel Cost includes employees only — subcontractors are planned via Resource Planning." testId="pc-empty" />
            )}
          </div>
        </>
      )}

      <PersonnelCostDetailPanel
        open={!!detail}
        onClose={() => setDetail(null)}
        employee={detail?.employee}
        month={detail?.month}
        year={year}
        paramLabels={data?.param_labels}
        isAdmin={isAdmin}
        onChanged={loadCosts}
      />
    </div>
  );
}
